import React from 'react';

/**
 * Format markdown-style text for display in React
 * Handles: **, *, ###, bullet points, and special characters
 */
export function formatMarkdownText(text: string): React.ReactNode[] {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Skip empty lines but add spacing
    if (line.trim() === '') {
      elements.push(<div key={key++} className="h-2" />);
      continue;
    }

    // Handle horizontal rules (═══ or ---)
    if (line.match(/^[═─\-]{3,}$/)) {
      elements.push(<hr key={key++} className="my-4 border-gray-300" />);
      continue;
    }

    // Handle headers (### or ##)
    if (line.startsWith('###')) {
      const content = line.replace(/^###\s*/, '');
      elements.push(
        <h4 key={key++} className="text-lg font-bold text-gray-900 mt-4 mb-2">
          {formatInlineMarkdown(content)}
        </h4>
      );
      continue;
    }

    if (line.startsWith('##')) {
      const content = line.replace(/^##\s*/, '');
      elements.push(
        <h3 key={key++} className="text-xl font-bold text-gray-900 mt-4 mb-2">
          {formatInlineMarkdown(content)}
        </h3>
      );
      continue;
    }

    if (line.startsWith('#')) {
      const content = line.replace(/^#\s*/, '');
      elements.push(
        <h2 key={key++} className="text-2xl font-bold text-gray-900 mt-4 mb-2">
          {formatInlineMarkdown(content)}
        </h2>
      );
      continue;
    }

    // Handle bullet points (- or •)
    if (line.match(/^\s*[-•]\s+/)) {
      const content = line.replace(/^\s*[-•]\s+/, '');
      elements.push(
        <div key={key++} className="flex items-start ml-4 my-1">
          <span className="text-blue-600 mr-2">•</span>
          <span className="text-gray-700">{formatInlineMarkdown(content)}</span>
        </div>
      );
      continue;
    }

    // Handle special formatted lines (FINAL RECOMMENDATION, etc.)
    if (line.includes('**') && line.includes(':')) {
      elements.push(
        <p key={key++} className="text-gray-800 my-2 font-medium">
          {formatInlineMarkdown(line)}
        </p>
      );
      continue;
    }

    // Regular paragraph
    elements.push(
      <p key={key++} className="text-gray-700 my-2">
        {formatInlineMarkdown(line)}
      </p>
    );
  }

  return elements;
}

/**
 * Format inline markdown (bold, italic, etc.)
 */
function formatInlineMarkdown(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let currentText = text;
  let key = 0;

  // Process bold text (**text**)
  const boldRegex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = boldRegex.exec(currentText)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      const beforeText = currentText.substring(lastIndex, match.index);
      parts.push(...formatItalicAndOther(beforeText, key++));
    }

    // Add bold text
    parts.push(
      <strong key={`bold-${key++}`} className="font-bold text-gray-900">
        {match[1]}
      </strong>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < currentText.length) {
    const remainingText = currentText.substring(lastIndex);
    parts.push(...formatItalicAndOther(remainingText, key++));
  }

  return parts.length > 0 ? parts : [text];
}

/**
 * Format italic and other inline styles
 */
function formatItalicAndOther(text: string, baseKey: number): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  
  // Process italic text (*text*)
  const italicRegex = /\*(.*?)\*/g;
  let lastIndex = 0;
  let key = baseKey;
  let italicMatch;

  while ((italicMatch = italicRegex.exec(text)) !== null) {
    // Add text before the match
    if (italicMatch.index > lastIndex) {
      parts.push(text.substring(lastIndex, italicMatch.index));
    }

    // Add italic text
    parts.push(
      <em key={`italic-${key++}`} className="italic">
        {italicMatch[1]}
      </em>
    );

    lastIndex = italicMatch.index + italicMatch[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

/**
 * Clean up special Unicode characters for better display
 */
export function cleanUnicodeText(text: string): string {
  return text
    .replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
      return String.fromCharCode(parseInt(hex, 16));
    })
    // Replace box drawing characters with standard ones
    .replace(/[═]/g, '=')
    .replace(/[─]/g, '-');
}
